import { useState } from "react";
import { skipToken, useMutation, useQuery } from "@apollo/client/react";
import { Banner } from "@leafygreen-ui/banner";
import { ConfirmationModal } from "@leafygreen-ui/confirmation-modal";
import { TextInput } from "@leafygreen-ui/text-input";
import { InlineCode } from "@leafygreen-ui/typography";
import { useToastContext } from "@evg-ui/lib/context/toast";
import {
  RepotrackerErrorQuery,
  RepotrackerErrorQueryVariables,
  SetLastRevisionMutation,
  SetLastRevisionMutationVariables,
  UserProjectSettingsPermissionsQuery,
  UserProjectSettingsPermissionsQueryVariables,
} from "gql/generated/types";
import { SET_LAST_REVISION } from "gql/mutations";
import {
  REPOTRACKER_ERROR,
  USER_PROJECT_SETTINGS_PERMISSIONS,
} from "gql/queries";
import { PortalBanner } from "../PortalBanner";
import styles from "./index.module.css";

interface RepotrackerBannerProps {
  projectIdentifier: string;
}
export const RepotrackerBanner: React.FC<RepotrackerBannerProps> = ({
  projectIdentifier,
}) => {
  const dispatchToast = useToastContext();
  const [openModal, setOpenModal] = useState(false);
  const [baseRevision, setBaseRevision] = useState("");

  const { data: repotrackerData } = useQuery<
    RepotrackerErrorQuery,
    RepotrackerErrorQueryVariables
  >(
    REPOTRACKER_ERROR,
    projectIdentifier ? { variables: { projectIdentifier } } : skipToken,
  );
  const hasRepotrackerError =
    repotrackerData?.project?.repotrackerError?.exists ?? false;

  const { data: permissionsData } = useQuery<
    UserProjectSettingsPermissionsQuery,
    UserProjectSettingsPermissionsQueryVariables
  >(
    USER_PROJECT_SETTINGS_PERMISSIONS,
    hasRepotrackerError ? { variables: { projectIdentifier } } : skipToken,
  );
  const isProjectAdmin =
    permissionsData?.user?.permissions?.projectPermissions?.edit ?? false;

  const [setLastRevision] = useMutation<
    SetLastRevisionMutation,
    SetLastRevisionMutationVariables
  >(SET_LAST_REVISION, {
    onCompleted: () => {
      dispatchToast.success(
        "Successfully updated merge base revision. The repotracker job has been scheduled to run.",
      );
    },
    onError: (err) => {
      dispatchToast.error(
        `Error when attempting to update merge base revision: ${err.message}`,
      );
    },
  });

  const resetModal = () => {
    setOpenModal(false);
    setBaseRevision("");
  };

  const onConfirm = () => {
    setLastRevision({
      variables: { projectIdentifier, revision: baseRevision },
      refetchQueries: ["RepotrackerError"],
    });
    resetModal();
  };

  if (!hasRepotrackerError) {
    return null;
  }
  return (
    <>
      <PortalBanner
        banner={
          <Banner data-testid="repotracker-error-banner" variant="danger">
            {isProjectAdmin ? (
              <span>
                The project was unable to build. Please specify a new base
                revision by clicking{" "}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- pre-existing violation, surfaced by the Emotion conversion */}
                <span
                  className={styles.modalTriggerText}
                  data-testid="repotracker-error-trigger"
                  onClick={() => setOpenModal(true)}
                >
                  here
                </span>
                .
              </span>
            ) : (
              "The project was unable to build. Please reach out to a project admin to fix."
            )}
          </Banner>
        }
      />
      <ConfirmationModal
        cancelButtonProps={{
          onClick: resetModal,
        }}
        confirmButtonProps={{
          children: "Confirm",
          disabled: baseRevision.length < 40,
          onClick: onConfirm,
        }}
        data-testid="repotracker-error-modal"
        open={openModal}
        setOpen={setOpenModal}
        title="Enter New Base Revision"
      >
        <div className={styles.modalDescription}>
          The current base revision{" "}
          <InlineCode>
            {repotrackerData?.project?.repotrackerError?.invalidRevision}
          </InlineCode>{" "}
          cannot be found on branch &apos;{repotrackerData?.project?.branch}
          &apos;. In order to resume tracking the repository, please enter a new
          base revision.
        </div>
        <TextInput
          description="Specify a full 40 character hash."
          label="Base Revision"
          onChange={(e) => setBaseRevision(e.target.value)}
          value={baseRevision}
        />
      </ConfirmationModal>
    </>
  );
};
